"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import TextareaAutosize from "react-textarea-autosize";
import { ArrowUp, Check, ChevronRight, RotateCcw, Sparkles, Square, X } from "lucide-react";
import avatarImg from "@/public/images/avatar.png";

// 本面板图标组：Lucide 线性，strokeWidth 1.5，尺寸 16 / 18
const ICON_STROKE = 1.5;
const MAX_LENGTH = 500;

const SUGGESTIONS = [
  "What are you studying?",
  "你写过哪些文章？",
  "How did you fix the iOS scroll-view gap?",
];

const LINK_PATTERN =
  /(https?:\/\/[^\s)）,，。、；;]+|\/(?:blog\/[\w-]+|message|project)\b|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

// 模型按人设在句末标注 [1] 或 [1][3]，也兼容 [1, 3]
const CITATION_PATTERN = /(\[\d{1,2}(?:\s*[,，]\s*\d{1,2})*\])/g;

const ACTIVITY_LABELS = {
  search_knowledge: "Searching my notes",
  leave_message: "Drafting your message",
  list_posts: "Checking my posts",
};

const linkClass =
  "underline decoration-slate-400 underline-offset-2 transition-colors hover:decoration-current dark:decoration-white/40";

const citationChipClass =
  "inline-flex h-4 min-w-4 items-center justify-center rounded bg-black/[0.07] px-1 text-[10px] font-semibold leading-none text-slate-600 dark:bg-white/10 dark:text-white/70";

function parseCitationNumbers(marker) {
  return marker
    .slice(1, -1)
    .split(/[,，]/)
    .map((value) => Number(value.trim()));
}

function citedNumbers(text) {
  return new Set([...text.matchAll(CITATION_PATTERN)].flatMap(([marker]) => parseCitationNumbers(marker)));
}

function renderLinks(text, keyPrefix, onNavigate) {
  return text.split(LINK_PATTERN).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (index % 2 === 0) return part;
    if (part.startsWith("/")) {
      return (
        <Link key={key} href={part} onClick={onNavigate} className={linkClass}>
          {part}
        </Link>
      );
    }
    if (!part.startsWith("http")) {
      return (
        <a key={key} href={`mailto:${part}`} className={linkClass}>
          {part}
        </a>
      );
    }
    return (
      <a key={key} href={part} target="_blank" rel="noopener noreferrer" className={linkClass}>
        {part}
      </a>
    );
  });
}

function CitationMarker({ source, onNavigate }) {
  const label = source.section ? `${source.title} > ${source.section}` : source.title;
  return (
    // 行内引用角标属于 WCAG 2.5.8 的行内例外，完整点击区由下方出处卡片提供
    <Link
      href={source.url}
      onClick={onNavigate}
      title={label}
      aria-label={`Source ${source.n}: ${label}`}
      className={`${citationChipClass} relative -top-1 mx-0.5 transition-colors hover:bg-black/[0.12] hover:text-slate-900 dark:hover:bg-white/20 dark:hover:text-white`}
    >
      {/* V3-EXEMPT: 系统级元素 / 引用角标 10px */}
      {source.n}
    </Link>
  );
}

// 助手回复是纯文本：[n] 变成出处角标，站内路径、外链和邮箱变成链接；无效编号直接丢弃
function renderRichText(text, { sourcesByN, onNavigate }) {
  return text.split(CITATION_PATTERN).flatMap((part, index) => {
    if (index % 2 === 0) return renderLinks(part, index, onNavigate);
    return parseCitationNumbers(part)
      .filter((n) => sourcesByN.has(n))
      .map((n) => (
        <CitationMarker key={`c-${index}-${n}`} source={sourcesByN.get(n)} onNavigate={onNavigate} />
      ));
  });
}

function SourceList({ sources, onNavigate }) {
  return (
    <div className="flex flex-col gap-1">
      {sources.map((source) => (
        <Link
          key={source.n}
          href={source.url}
          onClick={onNavigate}
          className="flex min-h-11 items-center gap-2 rounded-xl border border-black/[0.06] px-3 py-2 transition-colors hover:bg-black/[0.03] active:bg-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:active:bg-white/[0.08]"
        >
          <span className={`${citationChipClass} shrink-0`}>{source.n}</span>
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-medium leading-5 truncate text-slate-800 dark:text-white/85">
              {source.title}
            </span>
            {source.section && (
              <span className="block text-xs leading-4 truncate text-slate-500 dark:text-white/50">
                {source.section}
              </span>
            )}
          </span>
          <ChevronRight size={16} strokeWidth={ICON_STROKE} className="shrink-0 text-slate-400 dark:text-white/40" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

function describeError(error) {
  switch (error.code) {
    case "rate_limited":
      return {
        text: `Too many questions. Try again in ${Math.max(1, Math.ceil((error.retryAfterSec ?? 60) / 60))} min.`,
        canRetry: false,
      };
    case "twin_offline":
      return { text: "The twin is offline right now.", canRetry: false, showGuestbook: true };
    case "invalid_message":
      return { text: `Keep it under ${MAX_LENGTH} characters.`, canRetry: false };
    case "network":
      return { text: "Network issue. Check your connection.", canRetry: true };
    case "approval_pending":
      return { text: "Post or cancel the draft above first.", canRetry: false };
    case "login_required":
      return { text: "Sign in to post to the guestbook.", canRetry: false, showGuestbook: true };
    case "nothing_to_resume":
      return { text: "That draft expired. Ask me again to write a new one.", canRetry: false };
    case "invalid_decision":
      return { text: `Messages need 1–${MAX_LENGTH} characters.`, canRetry: false };
    default:
      return { text: "Something went wrong.", canRetry: true };
  }
}

const APPROVAL_STATUS_TEXT = {
  sending: "Posting…",
  sent: "Posted to the guestbook",
  cancelled: "Cancelled · not posted",
  failed: "Couldn't post. Try again later.",
  expired: "Draft expired · not posted",
};

/**
 * 留言确认卡：写操作前让访客看到草稿，可直接发布、改完再发或取消。
 * 未改动时回传 approve，改过则回传 edit + 新正文。
 */
function ApprovalCard({ approval, disabled, onResolve, onNavigate }) {
  const [text, setText] = useState(approval.draft);
  const isPending = approval.status === "pending";
  const trimmed = text.trim();
  const edited = trimmed !== approval.draft.trim();

  const post = () => {
    if (!trimmed) return;
    onResolve(edited ? { type: "edit", message: trimmed } : { type: "approve" });
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-black/[0.08] bg-white p-3 dark:border-white/10 dark:bg-white/[0.04]">
      <p className="text-xs font-medium text-slate-500 dark:text-white/50">Post this to my guestbook?</p>

      {isPending ? (
        <TextareaAutosize
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={MAX_LENGTH}
          minRows={2}
          maxRows={6}
          aria-label="Message draft"
          className="w-full resize-none rounded-xl border border-black/[0.08] bg-transparent px-3 py-2 text-sm leading-6 text-slate-900 outline-none transition-colors focus:border-black/20 dark:border-white/10 dark:text-white dark:focus:border-white/25"
        />
      ) : (
        <p
          className={`whitespace-pre-wrap break-words text-sm leading-6 ${
            approval.status === "sent" ? "text-slate-800 dark:text-white/85" : "text-slate-500 dark:text-white/45"
          }`}
        >
          {approval.finalText ?? approval.draft}
        </p>
      )}

      {isPending ? (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onResolve({ type: "reject" })}
            disabled={disabled}
            className="px-4 text-sm font-medium transition-colors rounded-full min-h-11 text-slate-600 hover:bg-black/5 active:bg-black/10 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-white/65 dark:hover:bg-white/10 dark:active:bg-white/15"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={post}
            disabled={disabled || !trimmed}
            className="px-4 text-sm font-semibold text-white transition rounded-full min-h-11 bg-slate-900 active:scale-[0.98] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:bg-white dark:text-slate-900"
          >
            {edited ? "Post edited" : "Post"}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-white/50" role="status">
          <span className="flex items-center gap-1.5">
            {approval.status === "sent" && <Check size={16} strokeWidth={ICON_STROKE} aria-hidden="true" />}
            {APPROVAL_STATUS_TEXT[approval.status]}
          </span>
          {approval.status === "sent" && (
            <Link href="/message" onClick={onNavigate} className={`${linkClass} flex min-h-11 items-center`}>
              View
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function TwinAvatar({ size = 32 }) {
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <Image
        src={avatarImg}
        alt="Hailin"
        width={size}
        height={size}
        className="object-cover rounded-full"
        unoptimized
      />
      <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-900 text-white ring-2 ring-[#fffdfa] dark:bg-white dark:text-slate-900 dark:ring-[#12161f]">
        {/* V3-EXEMPT: 小尺寸视觉补偿 / 10px 角标线宽 1.8 */}
        <Sparkles size={10} strokeWidth={1.8} aria-hidden="true" />
      </span>
    </span>
  );
}

function TypingDots() {
  return (
    <span className="flex items-center h-5 gap-1" aria-label="Thinking">
      <span className="w-1.5 h-1.5 rounded-full bg-current twin-typing-dot" />
      <span className="w-1.5 h-1.5 rounded-full bg-current twin-typing-dot" />
      <span className="w-1.5 h-1.5 rounded-full bg-current twin-typing-dot" />
    </span>
  );
}

function MessageBubble({ message, isStreaming, isBusy, onNavigate, onResolveApproval }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-slate-900 px-4 py-2 text-sm leading-6 text-white dark:bg-white dark:text-slate-900">
          {message.content}
        </p>
      </div>
    );
  }

  const sourcesByN = new Map((message.sources ?? []).map((source) => [source.n, source]));
  const cited = message.content ? citedNumbers(message.content) : new Set();
  const citedSources = (message.sources ?? []).filter((source) => cited.has(source.n));
  const activityLabel = ACTIVITY_LABELS[message.activity];
  const showBubble = Boolean(message.content) || (isStreaming && !message.approval);

  return (
    <div className="flex items-start gap-2">
      <TwinAvatar size={24} />
      <div className="flex w-full max-w-[85%] flex-col gap-2">
        {showBubble && (
          <div className="self-start whitespace-pre-wrap break-words rounded-2xl rounded-tl-md bg-black/[0.04] px-4 py-2 text-sm leading-6 text-slate-800 dark:bg-white/[0.06] dark:text-white/85">
            {message.content ? (
              renderRichText(message.content, { sourcesByN, onNavigate })
            ) : (
              <span className="flex items-center gap-2 text-slate-500 dark:text-white/50">
                <TypingDots />
                {activityLabel && <span>{activityLabel}</span>}
              </span>
            )}
          </div>
        )}
        {/* 流式输出结束后再出卡片，避免边打字边跳布局 */}
        {!isStreaming && citedSources.length > 0 && (
          <SourceList sources={citedSources} onNavigate={onNavigate} />
        )}
        {message.approval && (
          <ApprovalCard
            approval={message.approval}
            disabled={isBusy}
            onResolve={(decision) => onResolveApproval(message.id, decision)}
            onNavigate={onNavigate}
          />
        )}
      </div>
    </div>
  );
}

const iconButtonClass =
  "flex h-11 w-11 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-black/5 hover:text-slate-900 active:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:pointer-events-none disabled:opacity-40 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white dark:active:bg-white/15";

/**
 * 分身聊天面板：头部 / 消息列表（含空态、打字中、错误）/ 输入区。
 * 状态由 useTwinChat 提供，面板关闭后输入内容由父组件保留。
 */
export default function ChatPanel({ chat, draft, onDraftChange, onClose }) {
  const { messages, status, error, hasPendingApproval, send, stop, retry, reset, resolveApproval } = chat;
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const [stickToBottom, setStickToBottom] = useState(true);
  const isBusy = status === "waiting" || status === "streaming";
  const canSend = draft.trim().length > 0 && !isBusy && !hasPendingApproval;
  const errorInfo = error ? describeError(error) : null;

  // 触屏设备不自动聚焦，否则键盘一弹出就盖住推荐问题
  useEffect(() => {
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottom) list.scrollTop = list.scrollHeight;
  }, [messages, status, error, stickToBottom]);

  // 移动端抽屉会盖住刚打开的文章，点出处时收起；桌面端保持打开方便边读边问
  const closeOnMobile = () => {
    if (!window.matchMedia("(min-width: 640px)").matches) onClose();
  };

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    setStickToBottom(list.scrollHeight - list.scrollTop - list.clientHeight < 80);
  };

  const submit = (text) => {
    if (!text.trim() || isBusy) return;
    setStickToBottom(true);
    send(text);
    onDraftChange("");
  };

  const handleKeyDown = (event) => {
    // 中文输入法选词时的回车不发送
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit(draft);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-3 py-2 pl-4 pr-2 border-b border-black/[0.06] dark:border-white/[0.08]">
        <TwinAvatar size={32} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-5 truncate text-slate-900 dark:text-white">
            Hailin · AI twin
          </p>
          <p className="text-xs leading-4 truncate text-slate-500 dark:text-white/50">
            May get things wrong
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          disabled={messages.length === 0 && !error}
          className={iconButtonClass}
          aria-label="New chat"
          title="New chat"
        >
          <RotateCcw size={18} strokeWidth={ICON_STROKE} />
        </button>
        <button
          type="button"
          onClick={onClose}
          className={iconButtonClass}
          aria-label="Close chat"
          title="Close"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>
      </header>

      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 px-4 py-4 overflow-y-auto twin-scroll"
        aria-live="polite"
      >
        {messages.length === 0 && !error ? (
          <div className="flex flex-col items-start gap-4 pt-4">
            <TwinAvatar size={40} />
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold text-slate-900 dark:text-white">
                Hi, I&apos;m Hailin&apos;s AI twin.
              </p>
              <p className="text-sm leading-6 text-slate-600 dark:text-white/60">
                Ask about my study, stack or writing. 中文也可以。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => submit(suggestion)}
                  className="min-h-11 rounded-full border border-black/[0.08] px-4 text-sm text-slate-700 transition-colors hover:border-black/15 hover:bg-black/[0.03] active:bg-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-white/10 dark:text-white/75 dark:hover:border-white/20 dark:hover:bg-white/[0.05] dark:active:bg-white/10"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isStreaming={isBusy && index === messages.length - 1}
                isBusy={isBusy}
                onNavigate={closeOnMobile}
                onResolveApproval={resolveApproval}
              />
            ))}
          </div>
        )}

        {errorInfo && (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 px-4 py-2 mt-4 text-sm rounded-xl bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-200"
          >
            <span>{errorInfo.text}</span>
            {errorInfo.canRetry && error.retryText && (
              <button
                type="button"
                onClick={retry}
                className="font-semibold underline shrink-0 min-h-11 underline-offset-2"
              >
                Retry
              </button>
            )}
            {errorInfo.showGuestbook && (
              <Link href="/message" onClick={onClose} className="flex items-center font-semibold underline shrink-0 min-h-11 underline-offset-2">
                Leave a message
              </Link>
            )}
          </div>
        )}
      </div>

      <form
        className="px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))]"
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
      >
        <div className="flex items-end gap-2 rounded-2xl border border-black/[0.08] bg-white py-1 pl-4 pr-1 transition-colors focus-within:border-black/20 dark:border-white/10 dark:bg-white/[0.04] dark:focus-within:border-white/25">
          <TextareaAutosize
            ref={inputRef}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={MAX_LENGTH}
            minRows={1}
            maxRows={4}
            disabled={hasPendingApproval}
            placeholder={hasPendingApproval ? "Post or cancel the draft first" : "Ask me anything"}
            aria-label="Message"
            className="flex-1 py-2.5 text-sm leading-6 bg-transparent outline-none resize-none text-slate-900 placeholder:text-slate-400 disabled:cursor-not-allowed dark:text-white dark:placeholder:text-white/35"
          />
          {isBusy ? (
            <button
              type="button"
              onClick={stop}
              className="flex items-center justify-center text-white transition-transform rounded-full h-11 w-11 shrink-0 bg-slate-900 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:bg-white dark:text-slate-900"
              aria-label="Stop generating"
              title="Stop"
            >
              <Square size={14} strokeWidth={ICON_STROKE} fill="currentColor" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              className="flex items-center justify-center text-white transition rounded-full h-11 w-11 shrink-0 bg-slate-900 active:scale-95 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:bg-white dark:text-slate-900"
              aria-label="Send"
              title="Send"
            >
              <ArrowUp size={18} strokeWidth={ICON_STROKE} />
            </button>
          )}
        </div>
        {draft.length > MAX_LENGTH - 100 && (
          <p className="px-4 pt-1 text-xs text-right text-slate-500 dark:text-white/50">
            {draft.length}/{MAX_LENGTH}
          </p>
        )}
      </form>
    </div>
  );
}
